// src/providers/dataProvider.ts
import {
  DataProvider,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  GetManyParams,
  GetManyResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  UpdateParams,
  UpdateResult,
  UpdateManyParams,
  UpdateManyResult,
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  DeleteManyParams,
  DeleteManyResult,
  RaRecord,
  Identifier,
} from 'react-admin';
import queryString from 'query-string';
import { API_CONFIG } from '@/config/constants';
import { DateTimeSystem } from '@/utils/dateTimeSystem';

const apiUrl = API_CONFIG.BASE_URL;

// Tipo para las opciones de fetch
interface FetchOptions extends RequestInit {
  user?: {
    authenticated?: boolean;
  };
}

/**
 * Cliente HTTP con autenticación
 */
const httpClient = async (url: string, options: FetchOptions = {}): Promise<any> => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  const headers = options.headers as Headers;
  
  // Añadir token de autenticación si existe
  const token = localStorage.getItem('token');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Añadir Content-Type para peticiones con body (excepto FormData)
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  const response = await fetch(url, options);
  
  if (response.status === 204) {
    return { json: null };
  }
  
  // Manejar errores HTTP
  if (response.status < 200 || response.status >= 300) {
    // Manejar 401 - Token expirado
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    
    const body = await response.text();
    let error: any;
    
    try {
      error = JSON.parse(body);
    } catch (e) {
      error = { message: body };
    }
    
    throw new Error(error.message || response.statusText);
  }
  const contentLength = response.headers.get('Content-Length');

  if (response.status === 204 || contentLength === '0') {
    return { json: null }; // Devolvemos un objeto consistente que no rompa el resto del código.
  }
  
  const json = await response.json();
  return { json };
};

const prepareDataForBackend = (data: any): any => {
  const converted = DateTimeSystem.convertForBackend(data);
  const { id, createdAt, updatedAt, createdBy, updatedBy, _id, __typename, ...cleanData } = converted;
  return cleanData;
};

/**
 * Mapeo de nombres de recursos a endpoints de la API
 */
const resourceMap: Record<string, string> = {
  'locker-assignments': 'locker-assignments',
  'tag-types': 'tag-types',
  'locker-types': 'locker-types',
  'users': 'users',
  'hotels': 'hotels',
  'groups': 'groups',
  'lockers': 'lockers',
  'zones': 'zones',
  'tags': 'tags',
  'alerts': 'alerts',
  'system': 'system',
  'reports': 'reports',
  'hmis': 'hmis',
  'locker-controllers': 'locker-controllers',
};

/**
 * Obtiene el endpoint real para un recurso
 */
const getResourceEndpoint = (resource: string): string => {
  return resourceMap[resource] || resource;
};

/**
 * Data Provider para React Admin
 */
const dataProvider: DataProvider = {
  /**
   * GET_LIST - Obtener lista de registros
   */
/**
 * GET_LIST - Obtener lista de registros con debug de paginación
 */
getList: async <RecordType extends RaRecord = any>(
  resource: string,
  params: GetListParams
): Promise<GetListResult<RecordType>> => {
  const { pagination, sort, filter } = params;
  const { page, perPage } = pagination;
  const { field, order } = sort;
  
  // Construir query params
  const query: any = {
    page: page,
    pageSize: perPage,
    sortBy: field,
    sortOrder: order.toLowerCase(),
    ...filter,
  };
  
  // Si hay un filtro 'q', mapearlo a 'search' para el backend
  if (filter.q) {
    query.search = filter.q;
    delete query.q;
  }
  
  const endpoint = getResourceEndpoint(resource);
  const url = `${apiUrl}/${endpoint}?${queryString.stringify(query)}`;

  
  const { json } = await httpClient(url);
  
  
  // Manejar diferentes formatos de respuesta
  let data: RecordType[] = [];
  let total = 0;
  
  // Formato 1: { data: [...], pagination: { totalCount: N } }
  if (json.data && Array.isArray(json.data)) {
    data = json.data;
    if (json.pagination) {
      total = json.pagination.totalCount || json.pagination.total || data.length;
    } else {
      total = json.totalCount || json.total || data.length;
    }
  }
  // Formato 2: { items: [...], totalCount: N }
  else if (json.items && Array.isArray(json.items)) {
    data = json.items;
    total = json.totalCount || json.total || data.length;
  }
  // Formato 3: Array directo
  else if (Array.isArray(json)) {
    data = json;
    total = json.length;
  }
  // Formato 4: Respuesta con el nombre del recurso
  else if (json[resource] && Array.isArray(json[resource])) {
    data = json[resource];
    total = json.totalCount || json.total || data.length;
  }
  

  
  // Convertir fechas
  const convertedData = DateTimeSystem.convertFromBackend(data);
  
  return {
    data: convertedData as RecordType[],
    total: total,
  };
},

  /**
   * GET_ONE - Obtener un registro
   */
getOne: async <RecordType extends RaRecord = any>(
  resource: string,
  params: GetOneParams & { meta?: any }
): Promise<GetOneResult<RecordType>> => {
  const endpoint = getResourceEndpoint(resource);
  
  // Construir la URL base
  let url = `${apiUrl}/${endpoint}/${params.id}`;
  
  // Recursos que requieren hotelId en el query param
  const resourcesRequiringHotelId = [
    'locker-controllers',
    'lockers', 
    'zones',
    'locker-types',
    'hmis',
    'tags',
    'locker-assignments'
  ];
  
  // Si el recurso requiere hotelId y está en meta, añadirlo
  if (resourcesRequiringHotelId.includes(resource) && params.meta?.hotelId) {
    url = `${url}?hotelId=${params.meta.hotelId}`;
  } 
  // Para otros meta params genéricos
  else if (params.meta && Object.keys(params.meta).length > 0) {
    const queryParams = queryString.stringify(params.meta);
    url = `${url}?${queryParams}`;
  }
  
  const { json } = await httpClient(url);
  const data = json.data || json;
  const convertedData = DateTimeSystem.convertFromBackend(data);
  
  return {
    data: convertedData as RecordType,
  };
},

  /**
   * GET_MANY - Obtener múltiples registros por IDs
   */
 getMany: async <RecordType extends RaRecord = any>(
  resource: string,
  params: GetManyParams
): Promise<GetManyResult<RecordType>> => {
  const endpoint = getResourceEndpoint(resource);
  
  // ✅ SOLUCIÓN: Intentar getList con filtro de IDs primero
  try {
    // Intentar con filtro de IDs usando getList
    const query = {
      ids: params.ids.join(','), // Backend puede interpretar esto como filtro
      page: 1,
      pageSize: params.ids.length,
    };
    
    const url = `${apiUrl}/${endpoint}?${queryString.stringify(query)}`;
    const { json } = await httpClient(url);
    
    let data: RecordType[] = [];
    
    // Manejar diferentes formatos de respuesta
    if (json.data && Array.isArray(json.data)) {
      data = json.data;
    } else if (Array.isArray(json)) {
      data = json;
    }
    
    const convertedData = DateTimeSystem.convertFromBackend(data);
    
    return {
      data: convertedData as RecordType[],
    };
  } catch (error) {
    console.warn(`getList with IDs failed for ${resource}, falling back to individual requests:`, error);
    
    const promises = params.ids.map(async (id: Identifier) => {
      try {
        const { json } = await httpClient(`${apiUrl}/${endpoint}/${id}`);
        return (json.data || json) as RecordType;
      } catch (err) {
        console.warn(`Failed to fetch ${resource} with id ${id}:`, err);
        return null;
      }
    });
    
    const results = await Promise.all(promises);
    const validData = results.filter((item): item is Awaited<RecordType> => item !== null);
    const convertedData = DateTimeSystem.convertFromBackend(validData);
    
    return {
      data: convertedData as RecordType[],
    };
  }
},

  /**
   * GET_MANY_REFERENCE - Obtener registros relacionados
   */
  getManyReference: async <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyReferenceParams
  ): Promise<GetManyReferenceResult<RecordType>> => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    
    const query: any = {
      [params.target]: params.id,
      page: page,
      pageSize: perPage,
      sortBy: field,
      sortOrder: order.toLowerCase(),
      ...params.filter,
    };
    
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}?${queryString.stringify(query)}`;
    
    const { json } = await httpClient(url);
    
    let data: RecordType[] = [];
    let total = 0;
    
    if (json.data && Array.isArray(json.data)) {
      data = json.data;
      total = json.pagination?.totalCount || json.totalCount || json.total || data.length;
    } else if (Array.isArray(json)) {
      data = json;
      total = json.length;
    }
    
    const convertedData = DateTimeSystem.convertFromBackend(data);
    
    return {
      data: convertedData as RecordType[],
      total: total,
    };
  },

  /**
   * CREATE - Crear un registro
   */
 create: async <RecordType extends RaRecord = any>(
  resource: string,
  params: CreateParams
): Promise<CreateResult<RecordType>> => {
  // Manejar caso especial de reset-password
  if (resource.includes('/reset-password')) {
    const url = `${apiUrl}/${resource}`;
    const body = DateTimeSystem.convertForBackend(params.data);
    
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    
    // El backend devuelve { message, userId, userEmail }
    // Transformar para que tenga un campo 'id' que React Admin espera
    const responseData = json.data || json;
    const convertedData = {
      ...DateTimeSystem.convertFromBackend(responseData),
      id: responseData.userId || responseData.id || 'temp-id', // Asegurar que hay un id
      message: responseData.message,
      userEmail: responseData.userEmail
    };
    
    return {
      data: convertedData as RecordType,
    };
  }
  
  // Manejar caso especial de change-password (para el usuario actual)
  if (resource === 'auth/change-password') {
    const url = `${apiUrl}/${resource}`;
    const body = DateTimeSystem.convertForBackend(params.data);
    
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    
    // Transformar respuesta para incluir id
    const responseData = json.data || json;
    const convertedData = {
      ...DateTimeSystem.convertFromBackend(responseData),
      id: 'current-user', // ID temporal ya que es el usuario actual
    };
    
    return {
      data: convertedData as RecordType,
    };
  }
  
  // Comportamiento normal para otros recursos
  const endpoint = getResourceEndpoint(resource);
  const url = `${apiUrl}/${endpoint}`;
  
  const body = DateTimeSystem.convertForBackend(params.data);
  
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  
  const data = json.data || json;
  const convertedData = DateTimeSystem.convertFromBackend(data);
  
  return {
    data: convertedData as RecordType,
  };
},

  /**
   * UPDATE - Actualizar un registro
   */
  update: async (resource: string, params: UpdateParams): Promise<UpdateResult> => {
  const { id, data, meta } = params;
  
  // Manejar acciones especiales para HMIs
  if (resource === 'hmis' && meta?.action === 'generate-provisioning-code') {
    const url = `${apiUrl}/hmis/${id}/generate-provisioning-code`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    
    // Retornar el código de provisioning en el formato esperado
    return {
      data: {
        id,
        provisioningCode: json.data?.provisioningCode || json.provisioningCode,
        code: json.data?.code || json.code,
        expiresAt: json.data?.expiresAt || json.expiresAt,
        ...DateTimeSystem.convertFromBackend(json.data || json)
      }
    };
  }
  
  // Manejar reset de API key para HMIs
  if (resource === 'hmis' && meta?.action === 'reset-api-key') {
    const url = `${apiUrl}/hmis/${id}/reset-api-key`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return { data: DateTimeSystem.convertFromBackend(json.data || json) };
  }
  
  // AQUÍ ESTÁ EL FIX: Usar getResourceEndpoint para obtener el endpoint correcto
  const endpoint = getResourceEndpoint(resource); // <-- LÍNEA CRÍTICA
  const url = `${apiUrl}/${endpoint}/${id}`; // <-- USAR endpoint EN LUGAR DE resource
  const body = prepareDataForBackend(data);
  
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  
  return { data: DateTimeSystem.convertFromBackend(json.data || json) };
},


   generateProvisioningCode: async (hmiId: string) => {
    const url = `${apiUrl}/hmis/${hmiId}/generate-provisioning-code`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { 
      data: {
        provisioningCode: json.data?.provisioningCode || json.provisioningCode,
        code: json.data?.code || json.code,
        expiresAt: json.data?.expiresAt || json.expiresAt,
        ...DateTimeSystem.convertFromBackend(json.data || json)
      }
    };
  },

   resetHMIApiKey: async (hmiId: string, reason: string) => {
    const url = `${apiUrl}/hmis/${hmiId}/reset-api-key`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return { data: DateTimeSystem.convertFromBackend(json.data || json) };
  },

 testControllerConnection: async (controllerId: string) => {
    const url = `${apiUrl}/locker-controllers/${controllerId}/test-connection`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { data: DateTimeSystem.convertFromBackend(json.data || json) };
  },

/**
 * Ensure default texts exist for a hotel
 */
ensureDefaultTexts: async (hotelId: string, languageCode: string = 'EN') => {
  const url = `${apiUrl}/hotels/${hotelId}/ensure-default-texts`;
  
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({ languageCode })
  });
  
  return { data: DateTimeSystem.convertFromBackend(json) };
},

  // Métodos para LockerAssignments
 assignPhysicalLockers: async (assignmentId: string, lockerIds?: string[]) => {
  const url = `${apiUrl}/locker-assignments/${assignmentId}/assign-lockers`;
  
  const requestBody = (lockerIds && lockerIds.length > 0)
    ? { specificLockerIds: lockerIds }
    : {};
  
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
  
  return { data: DateTimeSystem.convertFromBackend(json.data || json) };
},


  releaseLockerAssignment: async (assignmentId: string) => {
    const url = `${apiUrl}/locker-assignments/${assignmentId}/release`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { data: DateTimeSystem.convertFromBackend(json.data || json) };
  },
  
  archiveLockerAssignment: async (assignmentId: string) => {
    const url = `${apiUrl}/locker-assignments/${assignmentId}/archive`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { data: DateTimeSystem.convertFromBackend(json.data || json) };
  },



  /**
   * UPDATE_MANY - Actualizar múltiples registros
   */
  updateMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateManyParams
  ): Promise<UpdateManyResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const body = DateTimeSystem.convertForBackend(params.data);
    
    // Intentar actualización masiva si la API lo soporta
    try {
      const url = `${apiUrl}/${endpoint}/bulk-update`;
      
      await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify({
          ids: params.ids,
          data: body,
        }),
      });
      
      return {
        data: params.ids,
      };
    } catch (error) {
      // Si no soporta bulk update, hacer múltiples updates
      const promises = params.ids.map((id: Identifier) =>
        httpClient(`${apiUrl}/${endpoint}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
          .then(() => id)
          .catch(() => null as Identifier | null)
      );
      
      const results = await Promise.all(promises);
      const successfulIds = results.filter((id): id is Identifier => id !== null);
      
      return {
        data: successfulIds,
      };
    }
  },

  /**
   * DELETE - Eliminar un registro
   */
  delete: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteParams
  ): Promise<DeleteResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${apiUrl}/${endpoint}/${params.id}`;
    
    await httpClient(url, {
      method: 'DELETE',
    });
    
    // React Admin espera que devolvamos el registro eliminado
    return {
      data: params.previousData as RecordType,
    };
  },

  /**
   * DELETE_MANY - Eliminar múltiples registros
   */
  deleteMany: async <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteManyParams
  ): Promise<DeleteManyResult<RecordType>> => {
    const endpoint = getResourceEndpoint(resource);
    
    try {
      // Intentar eliminación masiva si la API lo soporta
      const url = `${apiUrl}/${endpoint}/bulk-delete`;
      
      await httpClient(url, {
        method: 'DELETE',
        body: JSON.stringify({
          ids: params.ids,
        }),
      });
      
      return {
        data: params.ids,
      };
    } catch (error) {
      // Si no soporta bulk delete, hacer múltiples deletes
      const promises = params.ids.map((id: Identifier) =>
        httpClient(`${apiUrl}/${endpoint}/${id}`, {
          method: 'DELETE',
        })
          .then(() => id)
          .catch(() => null as Identifier | null)
      );
      
      const results = await Promise.all(promises);
      const successfulIds = results.filter((id): id is Identifier => id !== null);
      
      return {
        data: successfulIds,
      };
    }
  },
};

// Métodos adicionales específicos del proyecto
export const extendedDataProvider = {
  ...dataProvider,
  
  /**
   * Gets complete hotel settings
   */
  getHotelSettings: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/settings`;
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  },
  
  /**
   * Updates hotel settings
   */
  updateHotelSettings: async (hotelId: string, settingsData: any, logoFile: File | null = null) => {
    const url = `${apiUrl}/hotels/${hotelId}/settings`;
    
    const formData = new FormData();
    
    // Add basic fields
    if (settingsData.defaultLanguage) {
      formData.append('defaultLanguage', settingsData.defaultLanguage);
    }
    if (settingsData.installationCode) {
      formData.append('installationCode', settingsData.installationCode);
    }
    
    // Add notification settings
    if (settingsData.notifications) {
      if (settingsData.notifications.email) {
        formData.append('notifications.email', settingsData.notifications.email);
      }
      if (settingsData.notifications.types?.length > 0) {
        settingsData.notifications.types.forEach((type: string, index: number) => {
          formData.append(`notifications.types[${index}]`, type);
        });
      }
    }
    
    // Add logo file if provided
    if (logoFile) {
      formData.append('logoFile', logoFile);
    }
    
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}`);
    }
    
    const json = await response.json();
    return { data: DateTimeSystem.convertFromBackend(json) };
  },
  
  /**
   * Get available tag types for a hotel
   */
  getAvailableTagTypes: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/available-tag-types`;
    try {
      const { json } = await httpClient(url);
      return { data: DateTimeSystem.convertFromBackend(json.data || json) };
    } catch (error) {
      // Si el endpoint no existe, devolver array vacío
      console.warn('getAvailableTagTypes endpoint not available:', error);
      return { data: [] };
    }
  },
  
  /**
   * Get access logs for a locker assignment
   */
  getAssignmentAccessLogs: async (assignmentId: string, page: number = 1, pageSize: number = 25) => {
    // Construir query params
    const query = {
      page,
      pageSize
    };
    
    // Construir URL directamente - NO usar getOne porque añade el ID al final
    const url = `${apiUrl}/locker-assignments/${assignmentId}/access-log?${queryString.stringify(query)}`;
    
    try {
      // Usar httpClient directamente
      const { json } = await httpClient(url);
      
      // El backend devuelve formato: { data: [...], pagination: {...} }
      let logs = [];
      let pagination = null;
      
      if (json.data && Array.isArray(json.data)) {
        logs = json.data;
        pagination = json.pagination;
      } else if (Array.isArray(json)) {
        logs = json;
        pagination = {
          currentPage: page,
          pageSize: pageSize,
          totalCount: logs.length,
          totalPages: 1
        };
      }
      
      // Convertir fechas usando tu sistema
      const convertedLogs = DateTimeSystem.convertFromBackend(logs);
      
      return { 
        data: convertedLogs,
        pagination: pagination || {
          currentPage: page,
          pageSize: pageSize,
          totalCount: convertedLogs.length,
          totalPages: 1
        }
      };
    } catch (error) {
      console.error('Error fetching assignment access logs:', error);
      throw error;
    }
  },


  /**
   * Get supported tag types for a hotel
   */
  getSupportedTagTypes: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/supported-tag-types`;
    try {
      const { json } = await httpClient(url);
      return { data: DateTimeSystem.convertFromBackend(json.data || json) };
    } catch (error) {
      console.warn('getSupportedTagTypes endpoint not available:', error);
      return { data: [] };
    }
  },
  
  /**
   * Update supported tag types for a hotel
   */
  updateSupportedTagTypes: async (hotelId: string, tagTypeIds: string[]) => {
    const url = `${apiUrl}/hotels/${hotelId}/supported-tag-types`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify({ tagTypeIds }),
    });
    return { data: DateTimeSystem.convertFromBackend(json) };
  },
  
  /**
   * Get hotel logo
   */
  getHotelLogo: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/logo`;
    try {
      const { json } = await httpClient(url);
      return { data: DateTimeSystem.convertFromBackend(json) };
    } catch (error) {
      console.warn('getHotelLogo endpoint not available:', error);
      return { data: null };
    }
  },
  
  /**
   * Upload hotel logo
   */
  uploadHotelLogo: async (hotelId: string, logoFile: File) => {
    const url = `${apiUrl}/hotels/${hotelId}/logo`;
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}`);
    }
    
    const json = await response.json();
    return { data: DateTimeSystem.convertFromBackend(json) };
  },
  

  getHotelBasicInfo: async (hotelId: string) => {
  try {
    // Intentar primero el endpoint específico de UI
    const url = `${apiUrl}/hotels/${hotelId}`;
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json.data || json) };
  } catch (error) {
    // Si falla, intentar con el endpoint regular
    try {
      const url = `${apiUrl}/hotels/${hotelId}`;
      const { json } = await httpClient(url);
      return { data: DateTimeSystem.convertFromBackend(json.data || json) };
    } catch (secondError) {
      console.warn('Could not fetch hotel basic info:', error, secondError);
      throw error;
    }
  }
},


/**
 * Send test notification - VERSIÓN CORREGIDA
 */
sendTestNotification: async (hotelId: string, notificationType: string, testData?: any) => {
  const url = `${apiUrl}/hotels/${hotelId}/test-notification`;
  try {
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({
        notificationType,
        testData: testData || {
          guestName: 'Usuario de Prueba',
          lockerNumber: 'A001',
          timestamp: new Date().toISOString(),
        },
      }),
    });
    return { data: DateTimeSystem.convertFromBackend(json) };
  } catch (error) {
    console.error('Error sending test notification:', error);
    throw error;
  }
},


  /**
   * Reset user PIN
   */
  resetUserPin: async (userId: string, newPin: string) => {
    const url = `${apiUrl}/users/${userId}/reset-pin`;
    
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ newPin })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    if (response.status === 204) {
      return { data: { success: true } };
    }
    
    const jsonData = await response.json();
    return { data: jsonData };
  },
  
  /**
 * Reset user password (admin function - no current password required)
 */
resetUserPassword: async (userId: string, newPassword: string) => {
  const url = `${apiUrl}/users/${userId}/reset-password`;
  
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ newPassword })
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const jsonData = await response.json();
  return { data: jsonData };
},
  
  /**
   * Get current user info
   */
  getCurrentUser: async () => {
    const url = `${apiUrl}/users/me`;
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  },
  
  /**
   * Generate provisioning code for HMI
   */
  generateProvisioningCode: async (hmiId: string) => {
    const url = `${apiUrl}/hmis/${hmiId}/generate-provisioning-code`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { data: DateTimeSystem.convertFromBackend(json) };
  },

  /**
   * Restore default texts for a hotel
   */
  restoreDefaultTexts: async (hotelId: string, language: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/restore-default-texts`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({
      hotelId,
      language,
    }),
  });
  return { data: DateTimeSystem.convertFromBackend(json) };
},


/**
 * Create reference field for a hotel
 */
createReferenceField: async (hotelId: string, fieldData: any) => {
  const url = `${apiUrl}/hotels/${hotelId}/reference-fields`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({
      ...fieldData,
      hotelId,
    }),
  });
  return { data: DateTimeSystem.convertFromBackend(json) };
},

/**
 * Update reference field
 */
updateReferenceField: async (fieldId: string, fieldData: any) => {
  const url = `${apiUrl}/hotels/reference-fields/${fieldId}`;
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(fieldData),
  });
  return { data: DateTimeSystem.convertFromBackend(json) };
},

/**
 * Delete reference field
 */
deleteReferenceField: async (fieldId: string) => {
  const url = `${apiUrl}/hotels/reference-fields/${fieldId}`;
  await httpClient(url, {
    method: 'DELETE',
  });
  return { data: { id: fieldId } };
},

// 1. General Configuration
getGeneralConfiguration: async (hotelId: string) => {
  const url = `${apiUrl}/hotels/${hotelId}/general-config`;
  try {
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  } catch (error) {
    console.error('Error fetching general configuration:', error);
    throw error;
  }
},

updateGeneralConfiguration: async (hotelId: string, data: any) => {
  const url = `${apiUrl}/hotels/${hotelId}/general-config`;
  
  // Preparar los datos para el backend
  const payload = {
    // Basic Info
    name: data.name,
    address: data.address,
    installationCode: data.installationCode,
    groupId: data.groupId,
    
    // Language Settings
    defaultLanguage: data.defaultLanguage,
    
    // Operation Mode Settings
    operationMode: data.operationMode, // Ya viene como string del formulario
    emailAssignmentDuration: data.emailAssignmentDuration,
    codeExpirationMinutes: data.codeExpirationMinutes,
    lockerHoldMinutes: data.lockerHoldMinutes,
    
    // Tag Types
    supportedTagTypeIds: data.supportedTagTypeIds || []
  };
  
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  
  return { data: DateTimeSystem.convertFromBackend(json) };
},


// 2. Custom Texts (ya existe pero lo mejoramos)
getCustomTexts: async (hotelId: string) => {
  const url = `${apiUrl}/hotels/${hotelId}/custom-texts`;
  try {
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  } catch (error) {
    console.warn('getCustomTexts endpoint not available:', error);
    return { data: { textsByLanguage: {} } };
  }
},

updateCustomTexts: async (hotelId: string, customTexts: Record<string, Record<string, string>>) => {
  const url = `${apiUrl}/hotels/${hotelId}/custom-texts`;
  
  const payload = {
    customTexts: customTexts
  };
  
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  
  return { data: DateTimeSystem.convertFromBackend(json) };
},

createAssignmentTag: async (assignmentId: string, tagData: any) => {
    const url = `${apiUrl}/tags`;
    const payload = {
      assignmentId,
      tagTypeId: tagData.tagTypeId,
      data: tagData.data || null,
      description: tagData.description || null
    };

    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    const formattedData = DateTimeSystem.convertFromBackend(json);
   
    return { data: formattedData };
},


  /**
   * Delete tag from assignment
   */
  deleteAssignmentTag: async (tagId: string, hotelId: string) => {
    const url = `${apiUrl}/tags/${tagId}?hotelId=${hotelId}`;
    
    await httpClient(url, {
      method: 'DELETE',
    });
    
    return { data: { id: tagId } };
  },

  /**
   * Get tags for assignment
   */
  getAssignmentTags: async (assignmentId: string) => {
    const url = `${apiUrl}/tags?assignmentId=${assignmentId}`;
    
    const { json } = await httpClient(url);
    
    let data = [];
    if (json.data && Array.isArray(json.data)) {
      data = json.data;
    } else if (Array.isArray(json)) {
      data = json;
    }
    
    return { data: DateTimeSystem.convertFromBackend(data) };
  },





/**
 * Get zones for a hotel - VERSIÓN FINAL CORREGIDA
 */
getHotelZones: async (hotelId: string) => {
  if (!hotelId) {
    return { data: [] };
  }

  const query = {
    hotelId: hotelId,
    page: 1,
    pageSize: 100,
    sortBy: 'order',
    sortOrder: 'asc'
  };
  
  const url = `${apiUrl}/zones?${queryString.stringify(query)}`;
  
  try {
    const { json } = await httpClient(url);
    
    // Manejar el formato de respuesta del backend: { data: [...], pagination: {...} }
    let data: any[] = [];
    
    if (json.data && Array.isArray(json.data)) {
      data = json.data;
    } else if (Array.isArray(json)) {
      data = json;
    } else {
      console.warn('Unknown response format for zones:', json);
      data = [];
    }
    
    return { data: DateTimeSystem.convertFromBackend(data) };
  } catch (error) {
    const errorMessage = (error as Error)?.message || '';
    
    if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      console.info(`No zones found for hotel ${hotelId}`);
      return { data: [] };
    }
    
    if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
      console.warn(`No permission to access zones for hotel ${hotelId}`);
      return { data: [] };
    }
    
    console.error('Error fetching hotel zones:', error);
    throw error;
  }
},

/**
 * Create zone for a hotel - VERSIÓN CORREGIDA
 */
createHotelZone: async (hotelId: string, zoneData: any) => {
  const url = `${apiUrl}/zones`;
  
  // El backend espera un CreateZoneRequest con estos campos
  const createRequest = {
    hotelId: hotelId,
    name: zoneData.name,
    description: zoneData.description || null,
    order: zoneData.order || 0,
    isActive: zoneData.isActive !== undefined ? zoneData.isActive : true,
    parentZoneId: zoneData.parentZoneId || null
  };
  
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify(createRequest),
  });
  
  const data = json.data || json;
  return { data: DateTimeSystem.convertFromBackend(data) };
},

/**
 * Update hotel zone - VERSIÓN CORREGIDA
 */
updateHotelZone: async (zoneId: string, zoneData: any, hotelId: string) => { // <-- 1. Añade hotelId
  // 2. Añade hotelId a la URL como query param
  const url = `${apiUrl}/zones/${zoneId}?hotelId=${hotelId}`; 
  
  // El backend espera un UpdateZoneRequest con estos campos
  const updateRequest = {
    name: zoneData.name,
    description: zoneData.description || null,
    order: zoneData.order || 0,
    isActive: zoneData.isActive !== undefined ? zoneData.isActive : true,
    parentZoneId: zoneData.parentZoneId || null
  };
  
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(updateRequest),
  });
  
  const data = json.data || json;
  return { data: DateTimeSystem.convertFromBackend(data) };
},


/**
 * Delete hotel zone - NO REQUIERE CAMBIOS
 */
deleteHotelZone: async (zoneId: string, hotelId: string) => { // <-- 1. Añade hotelId
  // 2. Añade hotelId a la URL como query param
  const url = `${apiUrl}/zones/${zoneId}?hotelId=${hotelId}`; 
  
  await httpClient(url, {
    method: 'DELETE',
  });
  
  return { data: { id: zoneId } };
},

// 4. Reference Fields
getReferenceFields: async (hotelId: string) => {
  const url = `${apiUrl}/hotels/${hotelId}/reference-fields`;
  try {
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  } catch (error) {
    console.warn('getReferenceFields endpoint not available:', error);
    return { data: { referenceFields: [] } };
  }
},

updateReferenceFields: async (hotelId: string, referenceFields: any[]) => {
  const url = `${apiUrl}/hotels/${hotelId}/reference-fields`;
  
  const payload = {
    referenceFields: referenceFields
  };
  
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  
  return { data: DateTimeSystem.convertFromBackend(json) };
},

  getNotifierConfiguration: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config`; // ✅ Cambio: era notification-configuration
    const { json } = await httpClient(url, {
        method: 'GET',
    });
    return { data: json };
},

   getNotifierTemplates: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/notification-templates`; // ✅ Correcto
    const { json } = await httpClient(url, {
        method: 'GET',
    });
    return { data: json };
},

  updateNotifierApiKey: async (hotelId: string, data: { 
    apiKey: string; 
    accountName?: string; 
    force?: boolean 
}) => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config`; // ✅ Cambio: era notifier-apikey
    const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return { data: json };
},

  updateNotifierTemplate: async (hotelId: string, data: { 
    templateId: string; 
    defaultLanguage?: string 
}) => {
    const url = `${apiUrl}/hotels/${hotelId}/notification-template`; // ✅ Correcto
    const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return { data: json };
},

testNotifierConnection: async (hotelId: string) => {
    const url = `${apiUrl}/hotels/${hotelId}/notifier-config/test`; // ✅ Nuevo endpoint
    const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify({}),
    });
    return { data: json };
},

// 5. Notification Settings (ya existen, pero con la URL correcta)
getNotificationSettings: async (hotelId: string) => {
  const url = `${apiUrl}/hotels/${hotelId}/notifications`;
  try {
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  } catch (error) {
    console.warn('Error fetching notification settings:', error);
    return { 
      data: { 
        alertRecipients: [],
        minSeverityToNotify: 'WARNING'
      } 
    };
  }
},

updateNotificationSettings: async (hotelId: string, settings: any) => {
  const url = `${apiUrl}/hotels/${hotelId}/notifications`;
  
  const payload = {
    alertRecipients: settings.alertRecipients || [],
    minSeverityToNotify: settings.minSeverityToNotify || 'WARNING'
  };
  
  const { json } = await httpClient(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  
  return { data: DateTimeSystem.convertFromBackend(json) };
},


/**
 * Get branding configuration (color and logo)
 */
getBrandingConfiguration: async (hotelId: string) => {
  const url = `${apiUrl}/hotels/${hotelId}/branding`;
  try {
    const { json } = await httpClient(url);
    return { data: DateTimeSystem.convertFromBackend(json) };
  } catch (error) {
    console.warn('Error fetching branding configuration:', error);
    // Devolver valores por defecto si hay error
    return { 
      data: {
        primaryColor: '#F85100',
        logoBase64: null
      }
    };
  }
},


/**
 * Update branding configuration (color and logo)
 */
updateBrandingConfiguration: async (hotelId: string, primaryColor: string, logoFile: File | null = null) => {
  const url = `${apiUrl}/hotels/${hotelId}/branding`;
  
  const formData = new FormData();
  
  // Añadir color primario
  formData.append('PrimaryColor', primaryColor);
  
  // Añadir archivo de logo si existe
  if (logoFile) {
    formData.append('logoFile', logoFile);
  }
  
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      // NO añadir Content-Type - dejar que el navegador lo configure para FormData
    },
    body: formData
  });
  
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorText = await response.text();
      if (errorText) {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      }
    } catch (e) {
      // Si no es JSON, usar el status code
    }
    throw new Error(errorMessage);
  }
  
  const json = await response.json();
  return { data: DateTimeSystem.convertFromBackend(json) };
},

 /**
   * Get system information
   */
  getSystemInfo: async () => {
    const url = `${apiUrl}/system/info`;
    try {
      const { json } = await httpClient(url);
      // Los datos vienen directamente en json, no hay wrapper de "data"
      return { data: DateTimeSystem.convertFromBackend(json) };
    } catch (error) {
      console.error('Error fetching system info:', error);
      throw error;
    }
  },

  /**
   * Get system jobs status
   */
  getSystemJobs: async () => {
    const url = `${apiUrl}/system/jobs`;
    try {
      const { json } = await httpClient(url);
      // El endpoint devuelve directamente un array de jobs
      return { data: DateTimeSystem.convertFromBackend(json) };
    } catch (error) {
      console.error('Error fetching system jobs:', error);
      throw error;
    }
  },

  /**
   * Trigger a system job
   */
  triggerSystemJob: async (jobId: string) => {
    const url = `${apiUrl}/system/jobs/${jobId}/trigger`;
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return { data: DateTimeSystem.convertFromBackend(json) };
    } catch (error) {
      console.error('Error triggering system job:', error);
      throw error;
    }
  },
// En src/providers/dataProvider.ts, añade estos métodos al extendedDataProvider:

/**
 * Block a locker
 */
blockLocker: async (lockerId: string, reason: string) => {
  const url = `${apiUrl}/lockers/${lockerId}/block`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({
      reason: reason.trim()
    }),
  });
  return { data: lockerId };
},

/**
 * Unblock a locker
 */
unblockLocker: async (lockerId: string) => {
  const url = `${apiUrl}/lockers/${lockerId}/unblock`;
  const { json } = await httpClient(url, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return { data: lockerId };
},


/**
 * Get available lockers for assignment
 */
getAvailableLockers: async (assignmentId: string, hotelId?: string) => {
  if (!assignmentId) {
    throw new Error('assignmentId is required');
  }
  
  const queryParams = hotelId ? `?hotelId=${hotelId}` : '';
  const url = `${apiUrl}/locker-assignments/${assignmentId}/available-lockers${queryParams}`;
  
  try {
    const { json } = await httpClient(url);
    const data = json.data || json;
    const convertedData = DateTimeSystem.convertFromBackend(data);
    return { data: convertedData };
  } catch (error) {
    console.error('Error fetching available lockers:', error);
    throw error;
  }
},


}




export default extendedDataProvider;